import { FC, useState, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import classNames from 'classnames';

import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { PsyToolsId } from '../../meet/tools-panel/types';
import { useAppDispatch } from '../../../hooks/redux';

import { useGetCloudsHelperQuestionsQuery } from '../../../services/directus.api';
import { useUploadCloudsImageMutation } from '../../../services/clouds.api';
import Modal from '../../common/modal/modal';
import { CloseCircleCrossBtn } from '../../icons/close-circle-cross-btn';
import { withHoverIcon } from '../../../hoc/withHoverIcon';
import { IJournalResult } from '../../../store/types';
import { useFeatureFlagEnabled } from '../../../hooks/useFeatureFlagEnabled';
import { FEATURE_FLAG_TIPS } from '../../../constants/featureFlags';

import { Line } from './types';
import { CloudsViewer, exportCloudsImageToBlob } from './CloudsViewer';

// Хук для определения высоты viewport
const useViewportHeight = () => {
  const [height, setHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 0);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return height;
};

interface ICloudsPsyProps {
  roomName?: string;
  consultationId?: number;
  result?: IJournalResult;
  setResult?: React.Dispatch<React.SetStateAction<IJournalResult>>;
  onToolActivated?: (tool: string) => void;
  sendCloudsOpen?: () => void;
  sendCloudsClose?: () => void;
  clientLines?: Line[];
  clientSelectedImage?: number | null;
  clientFinished?: boolean;
  onFinishTool?: () => void;
  onCloseTool?: () => void;
  onSaveClouds?: () => Promise<void>;
}

const MAX_CLOUD_ID = 100;

const CloseCircleCrossBtnHover = withHoverIcon(CloseCircleCrossBtn);

// Компонент для отслеживания ошибок загрузки изображений
const CloudImageItem: FC<{
  imageId: number;
  index: number;
  onError: (imageId: number) => void;
}> = ({ imageId, index, onError }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Intersection Observer для lazy loading
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
          }
        });
      },
      { rootMargin: '100px' },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const smallUrl = `https://storage.yandexcloud.net/zingizi/clouds/small/${imageId}.jpg`;

  return (
    <div ref={containerRef} className="relative rounded-[10px] overflow-hidden" style={{ height: '180px' }}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {inView && (
        <img
          ref={imgRef}
          src={smallUrl}
          alt={`Облако ${index + 1}`}
          className="w-full h-full object-cover transition-opacity duration-300"
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => {
            // Если изображение не загрузилось, скрываем элемент
            onError(imageId);
          }}
          style={{ opacity: loaded ? 1 : 0 }}
        />
      )}
    </div>
  );
};

export const CloudsPsy: FC<ICloudsPsyProps> = ({
  roomName,
  consultationId,
  result,
  setResult,
  onToolActivated,
  sendCloudsOpen,
  sendCloudsClose,
  clientLines = [],
  clientSelectedImage = null,
  clientFinished = false,
  onFinishTool,
  onCloseTool,
  onSaveClouds,
}) => {
  const dispatch = useAppDispatch();
  const viewportHeight = useViewportHeight();
  const [active, setActive] = useState(false);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [isSliderVisible, setIsSliderVisible] = useState(true);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const isTipsEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TIPS);
  const [uploadCloudsImage] = useUploadCloudsImageMutation();
  const savedImageRef = useRef<string | null>(null);
  const previousClientFinishedRef = useRef<boolean>(clientFinished);
  // Создаем список всех ID изображений (1-100) без загрузки
  const cloudImageIds = useMemo(() => Array.from({ length: MAX_CLOUD_ID }, (_, i) => i + 1), []);

  // Отслеживаем изображения с ошибками загрузки, чтобы не показывать их
  const [failedImageIds, setFailedImageIds] = useState<Set<number>>(new Set());

  // Фильтруем список, исключая изображения с ошибками
  const validCloudImageIds = useMemo(
    () => cloudImageIds.filter((id) => !failedImageIds.has(id)),
    [cloudImageIds, failedImageIds],
  );

  const containerPadding = viewportHeight < 900 ? 'p-2 mt-2' : 'p-8';

  const fontSize = viewportHeight < 900 ? '16px' : '24px';

  const marginBottom = viewportHeight < 900 ? 'mb-1' : 'mb-4';
  const marginBottomParagraph = viewportHeight < 900 ? 'mb-1' : 'mb-2';

  const colorProps = useMemo(() => {
    return {
      defaultFill: '#828282',
      defaultFillBg: 'rgba(255, 255, 255, 0.8)',
      hoverFill: '#63656A',
      hoverFillBg: '#DEC05E',
    };
  }, []);

  // Используем imageId напрямую для получения URL
  const selectedCloudImageId =
    clientSelectedImage !== null && clientSelectedImage >= 0 && clientSelectedImage < cloudImageIds.length
      ? cloudImageIds[clientSelectedImage]
      : null;

  const selectedCloudBackgroundUrl = selectedCloudImageId
    ? `https://storage.yandexcloud.net/zingizi/clouds/${selectedCloudImageId}.jpg`
    : '';

  // Запрос отправляется когда клиент завершил рисунок
  const { data: helperQuestions } = useGetCloudsHelperQuestionsQuery(undefined, {
    skip: !clientFinished || !isTipsEnabled,
  });

  // Используем моковые данные пока не загрузились вопросы из API
  const questions = helperQuestions?.data?.map((q) => q.formatted_text) || [];

  const handleStartSession = () => {
    // Отправляем через data channel livekit
    sendCloudsOpen?.();
    dispatch(setActiveTool(PsyToolsId.CLOUDS));
    onToolActivated?.('clouds');
    setActive(true);
  };

  const handleFinishTool = () => {
    setActive(false);
    dispatch(setActiveTool(0));
    onFinishTool?.();
  };

  const handleRequestClose = () => {
    setIsConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setIsConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setIsConfirmCloseOpen(false);

    // Отправляем сообщение клиенту через data-channel для закрытия инструмента
    sendCloudsClose?.();
    handleFinishTool();

    // Закрываем инструмент в панели инструментов
    onCloseTool?.();
  };

  // Сброс слайдера когда клиент завершил рисунок
  useEffect(() => {
    if (clientFinished) {
      setIsSliderVisible(isTipsEnabled);
      setSliderIndex(0);
    }
  }, [clientFinished, isTipsEnabled]);

  useEffect(() => {
    // Обновляем ref для отслеживания предыдущего состояния
    const wasFinished = previousClientFinishedRef.current;
    previousClientFinishedRef.current = clientFinished;

    // Сохраняем результат в журнал только когда клиент завершил рисование и изображение еще не было сохранено
    // Важно: загрузка должна происходить только при переходе clientFinished с false на true,
    // а не при перезаходе на инструмент, когда clientFinished уже true
    if (
      clientFinished &&
      !wasFinished && // Ключевая проверка: загрузка только при переходе с false на true
      clientSelectedImage !== null &&
      clientLines.length > 0 &&
      result &&
      setResult &&
      consultationId
    ) {
      const cloudImageId = cloudImageIds[clientSelectedImage];
      const saveKey = `${clientSelectedImage}-${clientLines.length}`;

      // Проверяем, что это изображение еще не было сохранено
      if (savedImageRef.current === saveKey) {
        return;
      }

      // Дополнительная проверка: не загружать, если такое изображение уже есть в журнале
      const alreadySaved = result.clouds?.some(
        (entry) => entry.imageId === cloudImageId && entry.selected_image_index === clientSelectedImage && entry.image, // Проверяем, что файл уже был загружен
      );

      if (alreadySaved) {
        // Помечаем как сохраненное, чтобы не пытаться загрузить снова
        savedImageRef.current = saveKey;
        return;
      }

      if (!cloudImageId) {
        console.warn('Cloud image ID is not available for selected index', clientSelectedImage);
      } else {
        const saveCloudsImage = async () => {
          try {
            // Помечаем как сохраняемое, чтобы избежать повторных вызовов
            savedImageRef.current = saveKey;

            const imageUrl = `https://storage.yandexcloud.net/zingizi/clouds/${cloudImageId}.jpg`;

            const loadedImage = await new Promise<HTMLImageElement>((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = 'anonymous';

              const timeout = setTimeout(() => {
                reject(new Error('Таймаут загрузки изображения'));
              }, 10000);

              img.onload = () => {
                clearTimeout(timeout);
                if (img.width === 0 || img.height === 0) {
                  reject(new Error('Изображение имеет нулевой размер'));
                } else {
                  resolve(img);
                }
              };

              img.onerror = () => {
                clearTimeout(timeout);
                reject(new Error('Не удалось загрузить изображение'));
              };

              img.src = imageUrl;
            });

            // Экспортируем изображение в Blob
            const imageBlob = await exportCloudsImageToBlob(loadedImage, clientLines, 800, 600);

            // Генерируем UUID для имени файла
            const uuid = crypto.randomUUID();
            const filename = `${uuid}.png`;

            // Загружаем файл через API
            const uploadResult = await uploadCloudsImage({
              consultationId: consultationId.toString(),
              filename,
              file: imageBlob,
            }).unwrap();

            // Сохраняем имя файла (UUID с расширением) в поле image
            const cloudsEntry = {
              date_time: new Date().toISOString(),
              image: filename,
              imageId: cloudImageId,
              selected_image_index: clientSelectedImage,
              comment: '',
            };

            setResult((prevResult) => ({
              ...prevResult,
              clouds: [...(prevResult.clouds || []), cloudsEntry],
            }));
          } catch (error) {
            // В случае ошибки сбрасываем флаг, чтобы можно было повторить попытку
            savedImageRef.current = null;
            console.error('Failed to upload clouds image:', error);
          }
        };

        void saveCloudsImage();
      }
    }
  }, [
    clientFinished,
    clientSelectedImage,
    clientLines.length,
    setResult,
    consultationId,
    cloudImageIds,
    uploadCloudsImage,
  ]);

  return (
    <div className="flex h-full">
      {(!active || (active && clientSelectedImage === null)) && (
        <div className="w-[500px] min-w-[500px] h-full flex flex-col text-white rounded-l-[12px] font-comfortaa pt-6 px-6 relative">
          <p className="text-2xl font-bold mb-4">Облака</p>
          <div className="text-base font-manrope leading-relaxed mb-6">
            Предложите клиенту рассмотреть 50 изображений облаков и выбрать то, которое наиболее откликается ему в
            текущий момент.
            <br />
            <br />
            После выбора клиент сможет с помощью кисти обвести тот образ, который он видит в очертаниях облака.
            <br />
            <br />
            Используйте получившийся рисунок как проекцию для дальнейшей беседы.
          </div>

          <div className="flex-1 min-h-0">
            <div className="h-full overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-3 pb-[100px]">
                {validCloudImageIds.map((imageId, index) => (
                  <CloudImageItem
                    key={imageId}
                    imageId={imageId}
                    index={index}
                    onError={(id) => {
                      // Если изображение не загрузилось, добавляем его в список с ошибками
                      setFailedImageIds((prev) => new Set(prev).add(id));
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="absolute bottom-[24px] left-6 right-8 flex justify-start pointer-events-none">
            <button
              className={classNames(
                'w-full h-[57px] py-4 rounded-[16px] text-white font-comfortaa font-bold text-[20px] transition-colors pointer-events-auto',
                active
                  ? 'bg-[#C00000]/50 text-white cursor-not-allowed'
                  : 'bg-[#C00000] text-white cursor-pointer hover:bg-[#a00000]',
              )}
              onClick={handleStartSession}
              disabled={active}
              style={active ? { backgroundColor: 'rgba(192, 0, 0, 0.5)' } : undefined}
            >
              {active ? 'Клиент думает…' : 'Начать сессию с облаками'}
            </button>
          </div>
        </div>
      )}

      {active && clientSelectedImage !== null && (
        <div className="w-[830px] min-w-[830px] h-full flex flex-col text-white font-comfortaa p-6 relative">
          <h2 className="text-2xl font-bold mb-6">Облака</h2>

          {/* Показываем статус когда клиент рисует */}

          <div className="flex flex-col gap-6 flex-1 min-h-0 overflow-hidden">
            {/* Картинка - всегда видна, размер зависит от состояния подсказок */}
            <div
              className="bg-white rounded-lg overflow-hidden flex-shrink-0"
              style={
                isSliderVisible && clientFinished
                  ? { width: '500px', aspectRatio: '4/3' }
                  : { width: '782px', aspectRatio: '16/9' }
              }
            >
              {selectedCloudBackgroundUrl ? (
                <CloudsViewer backgroundImage={selectedCloudBackgroundUrl} lines={clientLines} />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Слайдер с вопросами (появляется после завершения) */}
            {clientFinished && isSliderVisible && isTipsEnabled && (
              <>
                <div
                  className={classNames(
                    'bg-white border-1 border-[#DEC05E] text-[#2C3038] rounded-[16px] flex flex-col min-h-0 overflow-hidden flex-shrink-0 relative',
                    containerPadding,
                  )}
                  style={{ flex: '1.2 1 50%', minHeight: 0 }}
                >
                  <button
                    onClick={() => setIsSliderVisible(false)}
                    className="absolute top-2 right-5 cursor-default"
                    aria-label="Закрыть подсказки"
                  >
                    <CloseCircleCrossBtnHover width={18} height={18} className="" {...colorProps} />
                  </button>
                  <div className="flex flex-col h-full min-h-0">
                    <div className="flex flex-col flex-1 min-h-0 gap-4 overflow-y-auto pr-2">
                      <div
                        className="leading-relaxed break-words min-w-0 pl-2"
                        style={{ fontSize, wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <p className={classNames('font-bold', marginBottom)} style={{ fontSize }}>
                          {helperQuestions?.data?.[sliderIndex]?.title}
                        </p>

                        <ReactMarkdown
                          components={{
                            p: (paragraphProps) => (
                              <p
                                className={classNames('mt-0 last:mb-0 break-words', marginBottomParagraph)}
                                style={{ fontSize, wordBreak: 'break-word', overflowWrap: 'break-word' }}
                                {...paragraphProps}
                              />
                            ),
                            ol: (olProps) => (
                              <ol
                                className="list-decimal pl-8"
                                style={{ fontSize, listStylePosition: 'outside' }}
                                {...olProps}
                              />
                            ),
                            li: (liProps) => (
                              <li
                                className="break-words"
                                style={{
                                  fontSize,
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  display: 'list-item',
                                }}
                                {...liProps}
                              />
                            ),
                          }}
                        >
                          {questions[sliderIndex]}
                        </ReactMarkdown>
                      </div>
                    </div>

                    <div className="mt-8 flex items-center justify-center gap-6 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setSliderIndex((prev) => Math.max(0, prev - 1))}
                        disabled={sliderIndex === 0}
                        aria-label="Предыдущий вопрос"
                        className={classNames(
                          'w-[30px] h-[30px] rounded-full flex items-center justify-center text-lg transition-colors cursor-pointer',
                          sliderIndex > 0
                            ? 'bg-[#E6E6E6] text-[#BFBFBF] hover:bg-[#d4d4d4]'
                            : 'bg-[#F3F3F3] text-[#C4C4C4] cursor-not-allowed',
                        )}
                      >
                        ←
                      </button>

                      <div className="flex items-center gap-6">
                        {questions.map((_, idx) => {
                          const isActive = idx === sliderIndex;

                          return (
                            <button
                              key={`question-dot-${idx}`}
                              type="button"
                              onClick={() => setSliderIndex(idx)}
                              aria-label={`Перейти к вопросу ${idx + 1}`}
                              className={classNames(
                                'rounded-full transition-colors cursor-pointer',
                                isActive ? 'bg-[#9C9C9C]' : 'bg-[#E6E6E6] hover:bg-[#d4d4d4]',
                              )}
                              style={{ width: isActive ? 20 : 14, height: isActive ? 20 : 14 }}
                            />
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        onClick={() => setSliderIndex((prev) => Math.min(questions.length - 1, prev + 1))}
                        disabled={sliderIndex === questions.length - 1}
                        aria-label="Следующий вопрос"
                        className={classNames(
                          'w-[30px] h-[30px] rounded-full flex items-center justify-center text-lg transition-colors cursor-pointer',
                          sliderIndex < questions.length - 1
                            ? 'bg-[#C00000] text-white hover:bg-[#a00000]'
                            : 'bg-[#E7B6B6] text-white/70 cursor-not-allowed',
                        )}
                      >
                        →
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Кнопки когда слайдер скрыт или клиент еще рисует */}
            {(!clientFinished || !isSliderVisible) && (
              <div className="absolute bottom-6 left-6 right-6 flex gap-3 flex-shrink-0">
                {clientFinished && !isSliderVisible && isTipsEnabled && (
                  <button
                    onClick={() => setIsSliderVisible(true)}
                    className="flex-1 py-3 rounded-[10px] bg-[#828282] text-white font-bold text-base cursor-pointer hover:bg-[#6e6e6e] transition-colors"
                  >
                    Открыть подсказки
                  </button>
                )}
                {clientFinished ? (
                  <button
                    onClick={handleRequestClose}
                    className="flex-1 py-3 rounded-[10px] bg-[#C00000] text-white font-bold text-base cursor-pointer hover:bg-[#a00000] transition-colors"
                  >
                    Завершить работу с инструментом
                  </button>
                ) : (
                  <button
                    className="w-full py-3 rounded-[10px] bg-[#C00000]/50 text-white font-bold text-base cursor-not-allowed"
                    disabled
                  >
                    Клиент рисует…
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        isConfirmCloseOpen={isConfirmCloseOpen}
        cancelCloseTool={handleConfirmClose}
        confirmCloseTool={handleCancelClose}
        confirmMessage="Вы действительно хотите закрыть инструмент «Облака»? Клиент перестанет видеть облака."
        title="Закрытие инструмента"
        cancelText="Оставить открытым"
        nonCancelText="Закрыть инструмент"
      />
    </div>
  );
};
